import React, { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronDown,
  Plus,
  Trash2,
  Play,
  Edit2,
  X,
  MessageSquare,
  Wand2,
  Users,
  Clock,
  Info,
  Loader2,
  AlertCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Image,
  Video,
  FileText,
  Headphones,
  Check,
} from 'lucide-react';
import { toast } from 'react-toastify';
import CronExpressionBuilder from './CronExpressionBuilder';
import { channels } from '../../../shared/constants';

const { ipcRenderer } = window.require('electron');

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const DAY_NAMES_FULL = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];

const formatCronToHumanReadable = (cronExpression) => {
  const parts = cronExpression.split(' ');
  if (parts.length < 5) return cronExpression;

  const [minute, hour, , , dayOfWeek] = parts;
  const timeStr = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;

  if (dayOfWeek === '*') {
    return `Todos os dias as ${timeStr}`;
  }

  if (dayOfWeek === '1-5') {
    return `Seg a Sex as ${timeStr}`;
  }

  if (dayOfWeek === '0-6' || dayOfWeek === '0,1,2,3,4,5,6') {
    return `Todos os dias as ${timeStr}`;
  }

  if (dayOfWeek.includes('-')) {
    const [start, end] = dayOfWeek.split('-').map(Number);
    return `${DAY_NAMES[start]} a ${DAY_NAMES[end]} as ${timeStr}`;
  }

  if (dayOfWeek.includes(',')) {
    const days = dayOfWeek.split(',').map(Number);
    const dayNames = days.map((d) => DAY_NAMES[d]).join(', ');
    return `${dayNames} as ${timeStr}`;
  }

  const dayNum = Number(dayOfWeek);
  if (!isNaN(dayNum) && dayNum >= 0 && dayNum <= 6) {
    return `${DAY_NAMES_FULL[dayNum]} as ${timeStr}`;
  }

  return cronExpression;
};

const MEDIA_TYPE_ICONS = {
  image: Image,
  video: Video,
  audio: Headphones,
  document: FileText,
};

const MEDIA_TYPE_COLORS = {
  image: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  video: 'text-red-400 bg-red-400/10 border-red-400/30',
  audio: 'text-purple-400 bg-purple-400/10 border-purple-400/30',
  document: 'text-green-400 bg-green-400/10 border-green-400/30',
};

const GroupSchedulerSection = ({ instanceId, settings }) => {
  const [showSchedulerSettings, setShowSchedulerSettings] = useState(() => {
    return localStorage.getItem('showGroupSchedulerSettings') === 'true';
  });
  const [groups, setGroups] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [executingPostId, setExecutingPostId] = useState(null);
  const [registeredMedia, setRegisteredMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  const [formData, setFormData] = useState({
    groupJid: '',
    groupName: '',
    cronExpression: '0 10 * * *',
    messageType: 'fixed',
    fixedMessage: '',
    aiPrompt: '',
    enabled: true,
    selectedMediaIds: [],
  });

  useEffect(() => {
    localStorage.setItem('showGroupSchedulerSettings', showSchedulerSettings.toString());
  }, [showSchedulerSettings]);

  useEffect(() => {
    if (showSchedulerSettings) {
      loadGroups();
      loadPosts();
      loadMedia();
    }
  }, [showSchedulerSettings, instanceId]);

  const loadMedia = async () => {
    setLoadingMedia(true);
    try {
      const result = await ipcRenderer.invoke('list-ai-media', instanceId);
      if (result.success) {
        const allMedia = [
          ...(result.mediaList || []),
          ...(result.mediaGroups || []).flatMap((group) =>
            (group.mediaItems || []).map((item) => ({
              ...item,
              groupName: group.title,
            }))
          ),
        ];
        setRegisteredMedia(allMedia);
      }
    } catch (error) {
      console.error('Erro ao carregar midias:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const toggleMediaSelection = (mediaId) => {
    setFormData((prev) => {
      const currentIds = prev.selectedMediaIds || [];
      const newIds = currentIds.includes(mediaId)
        ? currentIds.filter((id) => id !== mediaId)
        : [...currentIds, mediaId];
      return { ...prev, selectedMediaIds: newIds };
    });
  };

  const getMediaById = (mediaId) => {
    return registeredMedia.find((m) => m.id === mediaId);
  };

  const loadGroups = async () => {
    setLoadingGroups(true);
    setGroupsError(null);
    try {
      const result = await ipcRenderer.invoke(channels.GET_WHATSAPP_GROUPS, {
        instanceId,
      });
      if (result.success) {
        setGroups(result.groups);
        setGroupsError(null);
      } else {
        setGroupsError(result.error || 'Erro ao carregar grupos');
      }
    } catch (error) {
      setGroupsError('Erro ao carregar grupos');
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadPosts = async () => {
    setLoadingPosts(true);
    try {
      const result = await ipcRenderer.invoke(channels.GET_SCHEDULED_POSTS, {
        instanceId,
      });
      if (result.success) {
        setPosts(result.posts);
      }
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleGroupChange = (e) => {
    const selectedJid = e.target.value;
    const selectedGroup = groups.find((g) => g.jid === selectedJid);
    setFormData((prev) => ({
      ...prev,
      groupJid: selectedJid,
      groupName: selectedGroup?.name || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.groupJid) {
      toast.error('Selecione um grupo');
      return;
    }

    if (!formData.cronExpression) {
      toast.error('Configure a frequencia');
      return;
    }

    if (formData.messageType === 'fixed' && !formData.fixedMessage) {
      toast.error('Digite a mensagem');
      return;
    }

    if (formData.messageType === 'ai_generated' && !formData.aiPrompt) {
      toast.error('Digite o prompt para a IA');
      return;
    }

    try {
      if (editingPost) {
        const result = await ipcRenderer.invoke(channels.UPDATE_SCHEDULED_POST, {
          instanceId,
          postId: editingPost.id,
          updates: formData,
        });

        if (result.success) {
          toast.success('Postagem atualizada');
          await loadPosts();
          resetForm();
        } else {
          toast.error(result.error || 'Erro ao atualizar');
        }
      } else {
        const result = await ipcRenderer.invoke(channels.CREATE_SCHEDULED_POST, {
          instanceId,
          postData: formData,
        });

        if (result.success) {
          toast.success('Postagem agendada');
          await loadPosts();
          resetForm();
        } else {
          toast.error(result.error || 'Erro ao agendar');
        }
      }
    } catch (error) {
      toast.error('Erro ao salvar postagem');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Deseja realmente excluir esta postagem?')) return;

    try {
      const result = await ipcRenderer.invoke(channels.DELETE_SCHEDULED_POST, {
        instanceId,
        postId,
      });

      if (result.success) {
        toast.success('Postagem excluida');
        await loadPosts();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleToggle = async (post) => {
    try {
      const result = await ipcRenderer.invoke(channels.TOGGLE_SCHEDULED_POST, {
        instanceId,
        postId: post.id,
        enabled: !post.enabled,
      });

      if (result.success) {
        await loadPosts();
      }
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleExecuteNow = async (postId) => {
    if (executingPostId) return;

    setExecutingPostId(postId);
    try {
      const result = await ipcRenderer.invoke(channels.EXECUTE_SCHEDULED_POST_NOW, {
        instanceId,
        postId,
      });

      if (result.success) {
        toast.success('Mensagem enviada');
        await loadPosts();
      } else {
        toast.error(result.error || 'Erro ao enviar');
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setExecutingPostId(null);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      groupJid: post.groupJid,
      groupName: post.groupName,
      cronExpression: post.cronExpression,
      messageType: post.messageType,
      fixedMessage: post.fixedMessage || '',
      aiPrompt: post.aiPrompt || '',
      enabled: post.enabled,
      selectedMediaIds: post.selectedMediaIds || [],
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      groupJid: '',
      groupName: '',
      cronExpression: '0 10 * * *',
      messageType: 'fixed',
      fixedMessage: '',
      aiPrompt: '',
      enabled: true,
      selectedMediaIds: [],
    });
    setEditingPost(null);
    setShowCreateForm(false);
  };

  const formatTimeRemaining = (targetDate) => {
    const now = new Date();
    const target = new Date(targetDate);
    const diffMs = target - now;

    if (diffMs <= 0) return null;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    const isToday =
      now.getDate() === target.getDate() &&
      now.getMonth() === target.getMonth() &&
      now.getFullYear() === target.getFullYear();

    if (!isToday) return null;

    if (diffMinutes < 60) {
      return `Falta ${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minutos'}`;
    }

    const remainingMinutes = diffMinutes % 60;
    if (remainingMinutes === 0) {
      return `Falta ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    }

    return `Falta ${diffHours}h ${remainingMinutes}min`;
  };

  const formatNextExecution = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const formatted = d.toLocaleString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    const timeRemaining = formatTimeRemaining(date);
    if (timeRemaining) {
      return `${formatted} (${timeRemaining})`;
    }

    return formatted;
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-fuchsia-500/25 via-pink-500/20 to-rose-500/25 rounded-xl border border-fuchsia-500/50 shadow-lg hover:shadow-fuchsia-500/25 transition-shadow duration-200">
      <button
        type="button"
        className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
        onClick={() => setShowSchedulerSettings(!showSchedulerSettings)}
        aria-expanded={showSchedulerSettings}
      >
        <h3 className="font-medium flex items-center">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-fuchsia-500/40 via-pink-500/30 to-rose-500/40 shadow-lg border border-fuchsia-500/60 mr-3">
            <Calendar className="h-4 w-4 text-fuchsia-300" />
          </div>
          <span className="text-textPrimary">Postagens Agendadas</span>
          {posts.filter((p) => p.enabled).length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-fuchsia-500/30 text-fuchsia-300 rounded-full">
              {posts.filter((p) => p.enabled).length} ativas
            </span>
          )}
        </h3>
        <div
          className={`transform transition-transform duration-200 ${
            showSchedulerSettings ? 'rotate-180' : ''
          }`}
        >
          <ChevronDown className="h-5 w-5 text-textPrimary" aria-hidden="true" />
        </div>
      </button>

      <div
        className={`transition-all duration-300 ease-in-out ${
          showSchedulerSettings
            ? 'max-h-[2000px] opacity-100 overflow-y-auto'
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="px-4 pb-4">
          <div className="mb-4 p-3 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-fuchsia-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-textPrimary font-semibold text-sm mb-1">
                  Postagens Automaticas
                </h4>
                <p className="text-xs text-textSecondary">
                  Agende mensagens para serem enviadas automaticamente em grupos.
                  Ideal para promocoes diarias ou comunicados recorrentes.
                </p>
              </div>
            </div>
          </div>

          {!showCreateForm ? (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="w-full mb-4 flex items-center justify-center gap-2 p-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5" />
              Nova Postagem Agendada
            </button>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mb-4 p-4 bg-grayBase/80 rounded-lg border border-fuchsia-500/20"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-textPrimary">
                  {editingPost ? 'Editar Postagem' : 'Nova Postagem'}
                </h4>
                <button
                  type="button"
                  onClick={resetForm}
                  className="p-1 text-grayText hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-textPrimary">
                      <Users className="inline h-4 w-4 mr-1" />
                      Grupo
                    </label>
                    <button
                      type="button"
                      onClick={loadGroups}
                      disabled={loadingGroups}
                      className="flex items-center gap-1 text-xs text-fuchsia-400 hover:text-fuchsia-300 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3 w-3 ${loadingGroups ? 'animate-spin' : ''}`} />
                      Atualizar
                    </button>
                  </div>

                  {loadingGroups ? (
                    <div className="w-full rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-3 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 text-fuchsia-400 animate-spin" />
                      <span className="text-sm text-grayText">Carregando grupos...</span>
                    </div>
                  ) : groupsError ? (
                    <div className="w-full rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-3">
                      <div className="flex items-center gap-2 text-red-400">
                        <WifiOff className="h-4 w-4" />
                        <span className="text-sm font-medium">WhatsApp desconectado</span>
                      </div>
                      <p className="text-xs text-red-300/70 mt-1">
                        Conecte o WhatsApp para listar os grupos disponiveis
                      </p>
                    </div>
                  ) : groups.length === 0 ? (
                    <div className="w-full rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-3">
                      <div className="flex items-center gap-2 text-yellow-400">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Nenhum grupo encontrado</span>
                      </div>
                      <p className="text-xs text-yellow-300/70 mt-1">
                        Voce precisa participar de pelo menos um grupo no WhatsApp
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        value={formData.groupJid}
                        onChange={handleGroupChange}
                        className="w-full rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2 text-white focus:border-fuchsia-500 focus:outline-none"
                      >
                        <option value="">Selecione um grupo</option>
                        {groups.map((group) => (
                          <option key={group.jid} value={group.jid}>
                            {group.name} ({group.participantsCount} participantes)
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1 mt-1">
                        <Wifi className="h-3 w-3 text-emerald-400" />
                        <span className="text-xs text-emerald-400">{groups.length} grupos disponiveis</span>
                      </div>
                    </div>
                  )}
                </div>

                <CronExpressionBuilder
                  value={formData.cronExpression}
                  onChange={(cron) =>
                    setFormData((prev) => ({ ...prev, cronExpression: cron }))
                  }
                />

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Tipo de Mensagem
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, messageType: 'fixed' }))
                      }
                      className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                        formData.messageType === 'fixed'
                          ? 'bg-fuchsia-600 border-fuchsia-500 text-white'
                          : 'bg-fuchsia-500/10 border-fuchsia-500/30 text-grayText hover:border-fuchsia-500/50'
                      }`}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Mensagem Fixa
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          messageType: 'ai_generated',
                        }))
                      }
                      className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors ${
                        formData.messageType === 'ai_generated'
                          ? 'bg-fuchsia-600 border-fuchsia-500 text-white'
                          : 'bg-fuchsia-500/10 border-fuchsia-500/30 text-grayText hover:border-fuchsia-500/50'
                      }`}
                    >
                      <Wand2 className="h-4 w-4" />
                      IA Gera
                    </button>
                  </div>
                </div>

                {formData.messageType === 'fixed' ? (
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Mensagem
                    </label>
                    <textarea
                      value={formData.fixedMessage}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          fixedMessage: e.target.value,
                        }))
                      }
                      rows={4}
                      placeholder="Digite a mensagem que sera enviada..."
                      className="w-full rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2 text-white placeholder:text-grayText focus:border-fuchsia-500 focus:outline-none resize-none"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Prompt para a IA
                    </label>
                    <textarea
                      value={formData.aiPrompt}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          aiPrompt: e.target.value,
                        }))
                      }
                      rows={4}
                      placeholder="Ex: Gere uma mensagem de bom dia motivacional para o grupo..."
                      className="w-full rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2 text-white placeholder:text-grayText focus:border-fuchsia-500 focus:outline-none resize-none"
                    />
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-textPrimary">
                      <Image className="inline h-4 w-4 mr-1" />
                      Midias (opcional)
                    </label>
                    <button
                      type="button"
                      onClick={loadMedia}
                      disabled={loadingMedia}
                      className="flex items-center gap-1 text-xs text-fuchsia-400 hover:text-fuchsia-300 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3 w-3 ${loadingMedia ? 'animate-spin' : ''}`} />
                      Atualizar
                    </button>
                  </div>

                  {formData.selectedMediaIds?.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {formData.selectedMediaIds.map((mediaId) => {
                        const media = getMediaById(mediaId);
                        if (!media) return null;
                        const IconComponent = MEDIA_TYPE_ICONS[media.type] || FileText;
                        return (
                          <div
                            key={mediaId}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ${MEDIA_TYPE_COLORS[media.type] || 'text-gray-400 bg-gray-400/10 border-gray-400/30'}`}
                          >
                            <IconComponent className="h-3 w-3" />
                            <span className="truncate max-w-[100px]">{media.title}</span>
                            <button
                              type="button"
                              onClick={() => toggleMediaSelection(mediaId)}
                              className="ml-1 hover:text-red-400"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {loadingMedia ? (
                    <div className="flex items-center gap-2 text-sm text-grayText py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando midias...
                    </div>
                  ) : registeredMedia.length === 0 ? (
                    <div className="text-sm text-grayText py-2 px-3 bg-fuchsia-500/5 border border-fuchsia-500/20 rounded-lg">
                      <p>Nenhuma midia cadastrada.</p>
                      <p className="text-xs mt-1">Cadastre midias na secao "Midias da IA" para usa-las aqui.</p>
                    </div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-fuchsia-500/20 rounded-lg bg-fuchsia-500/5">
                      {registeredMedia.map((media) => {
                        const isSelected = (formData.selectedMediaIds || []).includes(media.id);
                        const IconComponent = MEDIA_TYPE_ICONS[media.type] || FileText;
                        return (
                          <button
                            type="button"
                            key={media.id}
                            onClick={() => toggleMediaSelection(media.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-fuchsia-500/10 last:border-b-0 ${
                              isSelected
                                ? 'bg-fuchsia-600/30'
                                : 'hover:bg-fuchsia-500/10'
                            }`}
                          >
                            <div className={`p-1 rounded ${MEDIA_TYPE_COLORS[media.type]?.split(' ')[1] || 'bg-gray-400/10'}`}>
                              <IconComponent className={`h-4 w-4 ${MEDIA_TYPE_COLORS[media.type]?.split(' ')[0] || 'text-gray-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{media.title}</p>
                              {media.groupName && (
                                <p className="text-xs text-grayText truncate">Grupo: {media.groupName}</p>
                              )}
                            </div>
                            {isSelected && (
                              <Check className="h-4 w-4 text-fuchsia-400 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 p-2 bg-grayLight hover:bg-grayLight/80 text-white rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 p-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-lg transition-colors"
                  >
                    {editingPost ? 'Salvar' : 'Agendar'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {loadingPosts ? (
            <div className="text-center py-4 text-grayText">Carregando...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-8 text-grayText">
              <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma postagem agendada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className={`p-4 rounded-lg border transition-colors ${
                    post.enabled
                      ? 'bg-fuchsia-500/10 border-fuchsia-500/30'
                      : 'bg-grayBase/50 border-grayLight/30 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-textPrimary">
                        {post.groupName || 'Grupo'}
                      </h5>
                      <p className="text-xs text-grayText flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatCronToHumanReadable(post.cronExpression)}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={post.enabled}
                        onChange={() => handleToggle(post)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-grayLight peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-fuchsia-600"></div>
                    </label>
                  </div>

                  <p className="text-sm text-textSecondary mb-2 line-clamp-2">
                    {post.messageType === 'fixed'
                      ? post.fixedMessage
                      : `[IA] ${post.aiPrompt}`}
                  </p>

                  {post.selectedMediaIds?.length > 0 && (
                    <div className="flex items-center gap-1 mb-2">
                      <Image className="h-3 w-3 text-fuchsia-400" />
                      <span className="text-xs text-fuchsia-400">
                        {post.selectedMediaIds.length} {post.selectedMediaIds.length === 1 ? 'midia' : 'midias'}
                      </span>
                    </div>
                  )}

                  {post.nextExecutions?.length > 0 && post.enabled && (
                    <p className="text-xs text-emerald-400 mb-2">
                      Proxima: {formatNextExecution(post.nextExecutions[0])}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(post)}
                      className="p-1.5 text-grayText hover:text-white hover:bg-fuchsia-500/20 rounded transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExecuteNow(post.id)}
                      disabled={executingPostId !== null}
                      className={`p-1.5 rounded transition-colors ${
                        executingPostId === post.id
                          ? 'text-emerald-400 bg-emerald-500/20'
                          : executingPostId !== null
                          ? 'text-grayText/50 cursor-not-allowed'
                          : 'text-grayText hover:text-emerald-400 hover:bg-emerald-500/20'
                      }`}
                      title={executingPostId === post.id ? 'Enviando...' : 'Enviar agora'}
                    >
                      {executingPostId === post.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(post.id)}
                      className="p-1.5 text-grayText hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupSchedulerSection;
